/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { NavLink as RouterNavLink } from 'react-router-dom';
import { Box, Center, Flex, Image, HStack } from 'chakra-v3';
import { Link } from '../Link';
import { Button } from '../buttons/Index';
import { MdLogout } from 'react-icons/md';
import { withBaseUrl } from '../../utils/url';

type MinimalUserHeaderProps = Record<string, never>;

/**
 * MinimalUserHeader component providing a simple header with logo and logout button.
 *
 * Displays the logo on the left and a logout button on the right.
 * Intended for use on pages where minimal navigation is desired.
 *
 * @remarks
 * Responsive breakpoints use theme breakpoints: mobile (480px), tablet (768px), laptop (1024px), xl (1280px).
 */
const MinimalUserHeader: React.FC<MinimalUserHeaderProps> = () => {
  return (
    <Box
      as="nav"
      bg="bg.primary"
      px={{ base: '16px', md: '32px', lg: '48px' }}
      py={{ base: '12px', md: '20px', lg: '24px' }}
      position="sticky"
      top={0}
      zIndex={1000}
    >
      <Flex align="center" w="100%" gap={{ base: 2, md: 4 }} maxW="1920px" mx="auto">
        {/* Logo */}
        <Link asChild variant="plain">
          <RouterNavLink to="/">
            <Flex
              as="span"
              cursor="pointer"
              _hover={{ opacity: 0.9 }}
              _active={{ opacity: 0.8 }}
              transition="opacity 0.2s ease"
              alignItems="center"
              bg="transparent"
              border="none"
              p={0}
              gap={{ base: '6px', md: '8px' }}
              flexShrink={0}
              minWidth={0}
            >
              <Center
                w="120px"
                h="36.219px"
                flexShrink={0}
                position="relative"
                overflow="hidden"
                aspectRatio="120.00/36.22"
              >
                <Image
                  src={withBaseUrl('/karya_evals_logo.svg')}
                  alt="Karya Logo"
                  w="100%"
                  h="100%"
                  objectFit="contain"
                  position="absolute"
                  inset={0}
                />
              </Center>
            </Flex>
          </RouterNavLink>
        </Link>

        {/* Logout Button */}
        <Button asChild variant="ghost" ml="auto" color="white" aria-label="Logout">
          <RouterNavLink to="/logout">
            <HStack gap={2}>
              <MdLogout size={24} color="currentColor" />
              <span>Logout</span>
            </HStack>
          </RouterNavLink>
        </Button>
      </Flex>
    </Box>
  );
};

export default MinimalUserHeader;
